import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Routine, RoutineStep } from '../types';
import { useAuth } from '../auth/AuthContext';
import { api } from '../api/client';
import { getPose } from '../data/poses';
import { STARTER_ROUTINES, getBuiltinRoutine, isBuiltinRoutine } from '../data/starterRoutines';

/** True when a step runs on both sides (per-step override, else the pose's flag). */
export function stepIsBilateral(step: RoutineStep): boolean {
  return step.bilateral ?? getPose(step.poseId)?.bilateral ?? false;
}

const BASE_KEY = 'yoga-me.routines.v1';
export const DEFAULT_HOLD_SECONDS = 30;

export interface RoutinesApi {
  routines: Routine[];
  get: (id: string) => Routine | undefined;
  create: (name: string, steps: RoutineStep[]) => Routine;
  update: (id: string, patch: Partial<Pick<Routine, 'name' | 'steps'>>) => void;
  setPublic: (id: string, isPublic: boolean) => void;
  remove: (id: string) => void;
}

// Storage is namespaced by identity: anonymous routines live under the base
// key; a signed-in user's cloud mirror lives under `${BASE_KEY}.<sub>`. This
// keeps one account's data from bleeding into another's on a shared browser.
const keyFor = (sub: string | null) => (sub ? `${BASE_KEY}.${sub}` : BASE_KEY);

function loadFrom(key: string): Routine[] {
  try {
    return JSON.parse(localStorage.getItem(key) || '[]') as Routine[];
  } catch {
    return [];
  }
}
const saveTo = (key: string, routines: Routine[]) =>
  localStorage.setItem(key, JSON.stringify(routines));

const byCreatedAt = (a: Routine, b: Routine) => a.createdAt.localeCompare(b.createdAt);

/**
 * Routine store. Signed out → localStorage exactly as before. Signed in →
 * DynamoDB via the sync API, with localStorage kept as an offline mirror. The
 * RoutinesApi surface is unchanged, so no component knows sync exists.
 */
export function useRoutines(): RoutinesApi {
  const { status, user } = useAuth();
  const sub = status === 'authenticated' && user ? user.sub : null;
  const [own, setOwn] = useState<Routine[]>(() => loadFrom(keyFor(null)));
  const subRef = useRef<string | null>(null);
  // Latest committed routines, for reading the current value synchronously in
  // event handlers. A React state updater runs asynchronously, so anything
  // computed *inside* one isn't available to code that runs right after the
  // setState call — which previously dropped the cloud sync on share/edit.
  const ownRef = useRef(own);
  useEffect(() => {
    ownRef.current = own;
  }, [own]);

  // On identity change: anonymous → show local; signed in → migrate local up,
  // then load the cloud copy as the source of truth.
  useEffect(() => {
    subRef.current = sub;
    if (!sub) {
      setOwn(loadFrom(keyFor(null)));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        let cloud = await api.listRoutines();
        const anon = loadFrom(keyFor(null));
        const cloudIds = new Set(cloud.map((r) => r.id));
        const toMigrate = anon.filter((r) => !cloudIds.has(r.id));
        await Promise.all(toMigrate.map((r) => api.putRoutine(r)));
        if (toMigrate.length) {
          localStorage.removeItem(keyFor(null)); // migrated — don't re-run or leak
          cloud = cloud.concat(toMigrate);
        }
        if (cancelled) return;
        cloud.sort(byCreatedAt);
        setOwn(cloud);
        saveTo(keyFor(sub), cloud);
      } catch (err) {
        console.error('[routines] cloud sync failed; using local cache', err);
        if (!cancelled) setOwn(loadFrom(keyFor(sub)));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sub]);

  // Write-through to the active identity's local mirror on every change.
  const persist = useCallback((updater: (prev: Routine[]) => Routine[]): Routine[] => {
    let result: Routine[] = [];
    setOwn((prev) => {
      result = updater(prev);
      saveTo(keyFor(subRef.current), result);
      return result;
    });
    return result;
  }, []);

  const get = useCallback(
    (id: string): Routine | undefined => getBuiltinRoutine(id) ?? own.find((r) => r.id === id),
    [own],
  );

  const create = useCallback(
    (name: string, steps: RoutineStep[]): Routine => {
      const routine: Routine = {
        id: crypto.randomUUID(),
        name: name.trim() || 'Untitled routine',
        steps,
        createdAt: new Date().toISOString(),
      };
      persist((prev) => [...prev, routine]);
      if (subRef.current) {
        api.putRoutine(routine).catch((e) => console.error('[routines] create sync failed', e));
      }
      return routine;
    },
    [persist],
  );

  const update = useCallback(
    (id: string, patch: Partial<Pick<Routine, 'name' | 'steps'>>): void => {
      if (isBuiltinRoutine(id)) return; // starter routines are read-only
      const target = ownRef.current.find((r) => r.id === id);
      if (!target) return;
      const updated = { ...target, ...patch };
      persist((prev) => prev.map((r) => (r.id === id ? updated : r)));
      if (subRef.current) {
        api.putRoutine(updated).catch((e) => console.error('[routines] update sync failed', e));
      }
    },
    [persist],
  );

  const setPublic = useCallback(
    (id: string, isPublic: boolean): void => {
      if (isBuiltinRoutine(id)) return; // starter routines can't be shared
      const target = ownRef.current.find((r) => r.id === id);
      if (!target) return;
      const updated = { ...target, public: isPublic };
      persist((prev) => prev.map((r) => (r.id === id ? updated : r)));
      if (subRef.current) {
        api.putRoutine(updated).catch((e) => console.error('[routines] publish sync failed', e));
      }
    },
    [persist],
  );

  const remove = useCallback(
    (id: string): void => {
      if (isBuiltinRoutine(id)) return; // starter routines can't be deleted
      persist((prev) => prev.filter((r) => r.id !== id));
      if (subRef.current) {
        api.deleteRoutine(id).catch((e) => console.error('[routines] delete sync failed', e));
      }
    },
    [persist],
  );

  // Starter routines always lead the list, then the user's own by creation time.
  const routines = useMemo(() => [...STARTER_ROUTINES, ...own], [own]);

  return { routines, get, create, update, setPublic, remove };
}

/** Sum of a routine's hold durations, in seconds (two-sided poses count twice). */
export function totalSeconds(routine: Routine): number {
  return routine.steps.reduce(
    (sum, s) => sum + (s.holdSeconds || 0) * (stepIsBilateral(s) ? 2 : 1),
    0,
  );
}

/** "5:30" style mm:ss. */
export function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
